import { surfaceDocumentSchema, type SurfaceDocument } from './types';

export interface SyncSurfaceResolveRequest {
  surfaceId: string;
  locale: string;
}

export interface SyncSurfaceSource {
  /** Raw source output. The shared resolver owns validation and narrowing. */
  resolve(request: SyncSurfaceResolveRequest): unknown;
}

/**
 * Documents a validator has already accepted, by reference.
 *
 * Membership is a fact, not an assumption: only a successful run of the shared
 * surface validator marks a document, and it marks the object it produced — so
 * "already validated" cannot be claimed by a caller that merely believes it.
 *
 * It exists because an arriving document was being parsed three times and
 * catalog-walked twice. The host's remote source validates on arrival to
 * classify the document, then hands the accepted object to the runtime provider,
 * which parsed it again and re-walked the same ~90 elements. Validation costs
 * roughly 15× composition and Hermes has no JIT, so that second pass was
 * milliseconds of synchronous JS-thread work on the app's first screen for an
 * answer already known.
 *
 * A `WeakSet` rather than a cache with a lifetime: the entry lives exactly as
 * long as the document object it describes, so nothing to invalidate and nothing
 * to leak. Keyed on identity deliberately — a mutated document is a different
 * fact, and zod hands back a fresh object from every parse, so identity is the
 * only honest key.
 */
const validated = new WeakSet<object>();

/** Record that this exact object passed validation. Validators only. */
export function markValidatedSurfaceDocument(document: SurfaceDocument): void {
  validated.add(document);
}

/** Whether this exact object is one a validator already accepted. */
export function isValidatedSurfaceDocument(document: unknown): document is SurfaceDocument {
  return typeof document === 'object' && document !== null && validated.has(document);
}

export function resolveSyncSurfaceDocument(
  source: SyncSurfaceSource,
  request: SyncSurfaceResolveRequest,
): SurfaceDocument {
  const document = source.resolve(request);
  // A document the validator already accepted is returned as-is. This is not a
  // relaxation of the seam's guarantee — the parse is skipped only for an object
  // that provably passed the stricter of the two checks — and it is what stops
  // the render boundary re-parsing every document the source hands it.
  if (isValidatedSurfaceDocument(document)) return document;
  return surfaceDocumentSchema.parse(document);
}
